import { useTranslation } from "react-i18next";
import { EditField } from "@/components/common/EntityEditForm";
import { useCandidateStatus } from "@/utils/candidate/statusUtils";
import { JOB_STATUS_VALUES } from "@/components/jobs/types/jobTypes";

export interface EntityEditFormConfig {
  entityType: 'candidate' | 'company' | 'job' | 'match';
  entity: any;
  onChange: (field: string, value: string | string[]) => void;
  tabType?: 'basic' | 'contact' | 'professional' | 'skills' | 'jobDetails' | 'qualifications' | 'additionalInfo' | 'all';
}

export const useEntityEditForm = ({ entityType, entity, onChange, tabType = 'all' }: EntityEditFormConfig) => {
  const { t } = useTranslation();
  const { getStatusOptions } = useCandidateStatus();

  const createField = (
    name: string,
    type: EditField['type'],
    label: string,
    placeholder?: string,
    required = false,
    options?: string[],
    allowCustom?: boolean,
    customField?: string,
    conditionalFields?: Record<string, string[]>,
    showCondition?: (entity: any) => boolean
  ): EditField => {
    // Handle special cases for job entity
    let value: string | string[];
    if (entityType === 'job') {
      if (name === 'companyId') {
        value = entity.company?.id || '';
      } else if (name === 'contactId') {
        value = entity.contactId || '';
      } else if (name === 'assignedRecruiter') {
        // Handle assignedRecruiter which is an object with id, name, first_name, last_name
        value = entity.assignedRecruiter?.id || '';
      } else {
        value = entity[name] || (type === 'skills' ? [] : '');
      }
    } else {
      value = entity[name] || (type === 'skills' ? [] : '');
    }

    // Format date values for HTML date input (YYYY-MM-DD)
    if (type === 'date' && value && typeof value === 'string') {
      value = value.split('T')[0]; // Extract just the date part from ISO timestamp
    }

    return {
      name,
      type,
      label,
      placeholder,
      required,
      value,
      onChange: (value) => onChange(name, value),
      options,
      allowCustom,
      customField,
      conditionalFields,
      showCondition
    };
  };

  const getCandidateFieldsByTab = (tab: string): EditField[] => {
    switch (tab) {
      case 'basic':
        return [
          createField(
            'name',
            'text',
            t('candidates.newCandidateModal.firstNameLabel'),
            t('candidates.newCandidateModal.firstNamePlaceholder'),
            true
          ),
          createField(
            'function',
            'text',
            t('candidates.newCandidateModal.functionLabel'),
            t('candidates.newCandidateModal.functionPlaceholder')
          ),
          createField(
            'status',
            'select',
            t('candidates.filters.statusLabel'),
            undefined,
            false,
            getStatusOptions(entity?.status).map(option => option.value),
            false,
            'candidate-status'
          ),
          createField(
            'unavailable_until',
            'date',
            t('candidates.unavailableUntil.dateLabel', 'Available From'),
            undefined,
            false
          )
        ];
        
      case 'contact':
        return [
          createField(
            'email',
            'email',
            t('candidates.newCandidateModal.emailLabel'),
            t('candidates.newCandidateModal.emailPlaceholder'),
            true
          ),
          createField(
            'phone',
            'tel',
            t('candidates.newCandidateModal.phoneLabel'),
            t('candidates.newCandidateModal.phonePlaceholder')
          ),
          createField(
            'linkedin',
            'url',
            t('candidates.newCandidateModal.linkedinLabel'),
            t('candidates.newCandidateModal.linkedinPlaceholder')
          )
        ];
        
      case 'professional':
        return [
          createField(
            'location',
            'text',
            t('candidates.newCandidateModal.locationLabel'),
            t('candidates.newCandidateModal.locationPlaceholder')
          ),
          createField(
            'manager',
            'assignee',
            t('candidates.newCandidateModal.assigneeLabel'),
            t('candidates.newCandidateModal.assigneePlaceholder')
          )
        ];
        
      case 'skills':
        return [
          createField(
            'skills',
            'skills',
            t('candidates.newCandidateModal.skillsLabel'),
            t('candidates.newCandidateModal.skillsPlaceholder')
          )
        ];
        
      default:
        return [];
    }
  };

  const getCandidateFields = (): EditField[] => [
    createField(
      'name',
      'text',
      t('common.name', 'Name'),
      t('candidates.newCandidateModal.firstNamePlaceholder'),
      true
    ),
    createField(
      'function',
      'text',
      t('candidates.newCandidateModal.functionLabel', 'Function'),
      t('candidates.newCandidateModal.functionPlaceholder')
    ),
    createField(
      'status',
      'select',
      t('candidates.filters.statusLabel', 'Status'),
      undefined,
      false,
      getStatusOptions(entity?.status).map(option => option.value),
      false,
      'candidate-status'
    ),
    createField(
      'unavailable_until',
      'date',
      t('candidates.unavailableUntil.dateLabel', 'Available From'),
      undefined,
      false
    ),
    createField(
      'email',
      'email',
      t('candidates.newCandidateModal.emailLabel'),
      t('candidates.newCandidateModal.emailPlaceholder'),
      true
    ),
    createField(
      'phone',
      'tel',
      t('candidates.newCandidateModal.phoneLabel'),
      t('candidates.newCandidateModal.phonePlaceholder')
    ),
    createField(
      'linkedin',
      'url',
      t('candidates.newCandidateModal.linkedinLabel'),
      t('candidates.newCandidateModal.linkedinPlaceholder')
    ),
    createField(
      'location',
      'text',
      t('candidates.newCandidateModal.locationLabel'),
      t('candidates.newCandidateModal.locationPlaceholder')
    ),
    createField(
      'manager',
      'assignee',
      t('candidates.newCandidateModal.assigneeLabel'),
      t('candidates.newCandidateModal.assigneePlaceholder')
    ),
    createField(
      'skills',
      'skills',
      t('candidates.newCandidateModal.skillsLabel'),
      t('candidates.newCandidateModal.skillsPlaceholder')
    )
  ];

  const getCompanyFields = (): EditField[] => [
    createField(
      'name',
      'text',
      t('companies.createModal.nameLabel'),
      t('companies.createModal.namePlaceholder'),
      true
    ),
    createField(
      'vat_number',
      'text',
      t('companies.createModal.vatNumberLabel'),
      t('companies.createModal.vatNumberPlaceholder')
    ),
    createField(
      'website_url',
      'text',
      t('companies.createModal.websiteUrlLabel', 'Company URL'),
      t('companies.createModal.websiteUrlPlaceholder', 'www.example.com')
    ),
    createField(
      'street',
      'text',
      t('companies.createModal.streetLabel'),
      t('companies.createModal.streetPlaceholder')
    ),
    createField(
      'city',
      'text',
      t('companies.createModal.cityLabel'),
      t('companies.createModal.cityPlaceholder')
    ),
    createField(
      'postal_code',
      'text',
      t('companies.createModal.postalCodeLabel'),
      t('companies.createModal.postalCodePlaceholder')
    ),
    createField(
      'country',
      'text',
      t('companies.createModal.countryLabel'),
      t('companies.createModal.countryPlaceholder')
    ),
    createField(
      'assignee',
      'assignee',
      t('companies.createModal.assigneeLabel'),
      t('companies.createModal.assigneePlaceholder')
    ),
    createField(
      'relationship',
      'select',
      t('companies.relationship.label'),
      undefined,
      false,
      ['customer', 'prospect', 'partner', 'none'],
      false,
      'company-relationship'
    )
  ];

  const getJobFieldsByTab = (tab: string): EditField[] => {
    switch (tab) {
      case 'basic':
        return [
          createField(
            'title',
            'text',
            t('jobs.formWizard.jobTitle'),
            t('jobs.formWizard.jobTitlePlaceholder'),
            true
          ),
          createField(
            'status',
            'select',
            t('jobs.formWizard.status'),
            t('jobs.formWizard.selectStatus'),
            false,
            [...JOB_STATUS_VALUES],
            false,
            'job-status'
          ),
          createField(
            'companyId',
            'company-selector',
            t('jobs.formWizard.company'),
            t('jobs.formWizard.selectCompany'),
            true
          ),
          createField(
            'contactId',
            'contact-selector',
            t('jobs.formWizard.contactPerson'),
            t('jobs.formWizard.selectContact')
          ),
          createField(
            'assignedRecruiter',
            'assignee',
            t('jobs.formWizard.assignedRecruiter'),
            t('jobs.formWizard.selectRecruiter')
          )
        ];
        
      case 'jobDetails':
        return [
          createField(
            'employmentType',
            'checkbox-group',
            t('jobs.formWizard.employmentType'),
            undefined,
            false,
            ['Contract', 'Freelance', 'Temporary', 'Internship'],
            false,
            undefined,
            {
              'Contract': ['contractDuration']
            }
          ),
          createField(
            'timeCommitment',
            'checkbox-group',
            t('jobs.formWizard.timeCommitment'),
            undefined,
            false,
            ['Full-time', 'Part-time']
          ),
          createField(
            'contractDuration',
            'text',
            t('jobs.formWizard.contractDuration'),
            t('jobs.formWizard.contractDurationPlaceholder')
          ),
          createField(
            'startDate',
            'date',
            t('jobs.formWizard.startDate'),
            undefined
          ),
          createField(
            'endDate',
            'date',
            t('jobs.formWizard.endDate'),
            undefined
          ),
          createField(
            'seniorityLevel',
            'select',
            t('jobs.formWizard.seniorityLevel'),
            t('jobs.formWizard.selectSeniorityLevel'),
            false,
            ['Entry Level', 'Junior', 'Mid-Level', 'Senior', 'Lead', 'Manager', 'Director', 'Executive']
          ),
          createField(
            'budget',
            'text',
            t('jobs.formWizard.budgetPerm'),
            t('jobs.formWizard.budgetPermPlaceholder'),
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            (entity) => {
              const employmentType = entity.employmentType || '';
              const timeCommitment = entity.timeCommitment || '';
              return employmentType.includes('Contract') || 
                     employmentType.includes('Temporary') ||
                     timeCommitment === 'Full-time' ||
                     timeCommitment === 'Part-time';
            }
          ),
          createField(
            'budgetFreelance',
            'text',
            t('jobs.formWizard.budgetFreelance'),
            t('jobs.formWizard.budgetFreelancePlaceholder'),
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            (entity) => {
              const employmentType = entity.employmentType || '';
              return employmentType.includes('Freelance');
            }
          ),
          createField(
            'locationType',
            'select',
            t('jobs.formWizard.locationType'),
            t('jobs.formWizard.selectLocationType'),
            false,
            ['On-site', 'Remote', 'Hybrid']
          ),
          createField(
            'workplaceBreakdown',
            'text',
            t('jobs.formWizard.workplaceBreakdown'),
            t('jobs.formWizard.workplaceBreakdownPlaceholder'),
            false,
            undefined,
            undefined,
            undefined,
            undefined,
            (entity) => {
              const locationType = entity.locationType || '';
              return locationType === 'Hybrid';
            }
          )
        ];
        
      case 'qualifications':
        return [
          createField(
            'educationLevel',
            'select',
            t('jobs.formWizard.educationLevel'),
            t('jobs.formWizard.selectEducationLevel'),
            false,
            ['HIGH_SCHOOL', 'ASSOCIATE', 'BACHELOR', 'MASTER', 'DOCTORATE', 'NOT_REQUIRED']
          ),
          createField(
            'yearsOfExperience',
            'text',
            t('jobs.formWizard.yearsOfExperience'),
            t('jobs.formWizard.yearsOfExperiencePlaceholder')
          )
        ];
        
      case 'skills':
        return [
          createField(
            'hardSkills',
            'skills',
            t('jobs.formWizard.hardSkills'),
            t('jobs.formWizard.hardSkillsPlaceholder')
          ),
          createField(
            'softSkills',
            'skills',
            t('jobs.formWizard.softSkills'),
            t('jobs.formWizard.softSkillsPlaceholder')
          ),
          createField(
            'skills',
            'skills',
            t('jobs.formWizard.skills'),
            t('jobs.formWizard.skillsPlaceholder')
          ),
          createField(
            'cultureTags',
            'skills',
            t('jobs.formWizard.cultureTags'),
            t('jobs.formWizard.cultureTagsPlaceholder')
          )
        ];
        
      case 'additionalInfo':
        return [
          createField(
            'applicationDeadline',
            'date',
            t('jobs.formWizard.applicationDeadline'),
            undefined
          ),
          createField(
            'jobPostingDate',
            'date',
            t('jobs.formWizard.jobPostingDate'),
            undefined
          ),
          createField(
            'source',
            'text',
            t('jobs.formWizard.source'),
            t('jobs.formWizard.sourcePlaceholder')
          )
        ];
        
      default:
        return [];
    }
  };

  const getJobFields = (): EditField[] => [
    createField(
      'title',
      'text',
      t('jobs.form.titleLabel'),
      t('jobs.form.titlePlaceholder'),
      true
    ),
    createField(
      'description',
      'textarea',
      t('jobs.form.descriptionLabel'),
      t('jobs.form.descriptionPlaceholder')
    ),
    createField(
      'location',
      'text',
      t('jobs.form.locationLabel'),
      t('jobs.form.locationPlaceholder')
    ),
    createField(
      'salary_min',
      'text',
      t('jobs.form.salaryMinLabel'),
      t('jobs.form.salaryMinPlaceholder')
    ),
    createField(
      'salary_max',
      'text',
      t('jobs.form.salaryMaxLabel'),
      t('jobs.form.salaryMaxPlaceholder')
    ),
    createField(
      'assignedRecruiter',
      'assignee',
      t('jobs.form.assignedRecruiterLabel'),
      t('jobs.form.assignedRecruiterPlaceholder')
    )
  ];

  const getFields = (): EditField[] => {
    if (entityType === 'job' && tabType !== 'all') {
      return getJobFieldsByTab(tabType);
    }
    
    if (entityType === 'candidate' && tabType !== 'all') {
      return getCandidateFieldsByTab(tabType);
    }
    
    switch (entityType) {
      case 'candidate':
        return getCandidateFields();
      case 'company':
        return getCompanyFields();
      case 'job':
        return getJobFieldsByTab('basic'); // Default to basic tab
      case 'match':
        return getJobFields(); // For now, using job fields as template
      default:
        return [];
    }
  };

  const getTitle = (): string => {
    switch (entityType) {
      case 'candidate':
        return '';
      case 'company':
        return t('companies.profile.companyInfo');
      case 'job':
        return t('jobs.profile.jobInfo');
      case 'match':
        return t('matches.profile.matchInfo');
      default:
        return '';
    }
  };

  const shouldShowSkillsAtBottom = (): boolean => {
    return entityType === 'candidate';
  };

  return {
    fields: getFields(),
    title: getTitle(),
    showSkillsAtBottom: shouldShowSkillsAtBottom(),
    gridCols: 2 as const
  };
}; 